import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useAccount, useBalance, useReadContract} from 'wagmi';
import {type Address, createPublicClient, formatUnits, getAddress, http} from 'viem';
import {
  type LockRewardInfo,
  LRSNODE_CONTRACT_CONFIG,
  LRSNODE_V3_CONTRACT_CONFIG,
  type NodeInfo, NodeInfoV3,
  type ReferNodeInfo
} from '@/configs/lrsnode.config';
import {LRS_STAKING_ABI, LRS_STAKING_CONTRACT, REWARD_POOL_TOKEN} from '@/abis/lrs-staking';
import {larissaChain} from '@/configs/chain';
import {publicClient} from '@/configs/client';
import {MINT_STATS_ABI, MINT_STATS_CONTRACT} from '@/abis/mint-stats';

// ============================================================================
// TYPES
// ============================================================================

export interface StatsData {
  activeWallets: number;
  price: number;
  marketCap: number;
  circulatingSupply: number;
  lrsNodes: number;
  totalValueLocked: number;
  tvlPercentage: number;
  isLoading: boolean;
  error: string | null;
}

export interface BlockchainData {
  currentBlockReward: number;
  currentBlock: number;
  circulatingSupply: number;
  totalSupply: number;
  maxSupply: number;
  isLoading: boolean;
  error: string | null;
}

export interface SiloStats {
  totalSpace: number;
  usedSpace: number;
  freeSpace: number;
  activeNodes: number;
  networkUptime: number;
  dataTransfer: number;
  storageCost: number;
  countries: Array<{
    countryCode: string;
    nodes: number;
    percentage: number;
  }>;
  isLoading: boolean;
  error: string | null;
}

export interface LRSStakeInfo {
  stakeID: bigint;
  stakeAddress: string;
  stakeAmt: bigint;
  stakeStartTime: bigint;
  stakeStatus: bigint;
}

export interface GlobalDataContextType {
  // Stats Data
  stats: StatsData;

  // Blockchain Data
  blockchain: BlockchainData;

  // Silo Stats
  silo: SiloStats;

  // Staking Data
  staking: {
    totalValueLocked: number;
    circulatingSupply: number;
    lrsTokenBalance: bigint | undefined;
    rewardPoolBalance: bigint | undefined;
    totalStakeCount: bigint | undefined;
    userStakeIds: bigint[] | undefined;
    currentStakeAmount: bigint | undefined;
    userStakes: LRSStakeInfo[];
    rewardPerStake: number;
    calculatedAPY: number;
    refetchLrsBalance: () => void;
    refetchUserStakeIds: () => void;
    refetchAll: () => void;
    isLoading: boolean;
  };

  // LRSNode Data
  lrsnode: {
    userReferCode: string | undefined;
    slotData: bigint | undefined;
    walletBalance: bigint | undefined;
    nodes: NodeInfo[];
    referNodes: ReferNodeInfo[];
    lockRewards: LockRewardInfo[];
    isLoadingNodes: boolean;
    isLoadingReferNodes: boolean;
    isLoadingLockRewards: boolean;
    refreshNodes: () => Promise<void>;
  };

  // LRSNode Data
  lrsnodeV3: {
    userReferCode: string | undefined;
    slotData: bigint | undefined;
    walletBalance: bigint | undefined;
    nodes: NodeInfoV3[];
    referNodes: ReferNodeInfo[];
    isLoadingNodes: boolean;
    isLoadingReferNodes: boolean;
    refreshNodes: () => Promise<void>;
  };

  // Bridge Data
  bridge: {
    transactionHistory: Array<{
      id: string;
      sourceChain: string;
      destinationChain: string;
      token: string;
      amount: string;
      status: string;
      timestamp: number;
      sourceHash?: string;
      destinationHash?: string;
      fromAddress?: string;
      toAddress?: string;
    }>;
    isLoadingHistory: boolean;
    historyError: string | null;
    refetchHistory: () => void;
    // Bridge Scan API
    chains: Array<{
      id: number;
      name: string;
      icon?: string;
    }>;
    isLoadingChains: boolean;
    fetchBridgeTransactions: (filters: {
      page?: number;
      limit?: number;
      sourceChain?: string;
      startDate?: string;
      endDate?: string;
      status?: string;
      address?: string;
      search?: string;
    }) => Promise<{
      status: boolean;
      data: Array<{
        id: string;
        sourceTxHash?: string;
        sourceChain: {
          name: string;
          icon?: string;
        };
        destChain: {
          name: string;
          icon?: string;
        };
        fromAddress?: string;
        toAddress?: string;
        token?: {
          symbol: string;
          decimals?: number;
          logoURI?: string;
        };
        amount?: string;
        status: string;
        statusText: string;
        formattedDate?: string;
      }>;
      total: number;
      totalPages: number;
      message?: string;
    }>;
    processTransaction: (txHash: string, chainId?: number) => Promise<{
      status: boolean;
      message: string;
      data?: {
        chainId: number;
        chainName: string;
        transactionHash: string;
        record: any;
      };
    }>;
  };

  // Mint Stats Data
  mintStats: {
    total1Minted: number;
    total2Minted: number;
    total3Minted: number;
    totalNFT: number;
    isLoading: boolean;
    refetch: () => void;
  };

  // Global Functions
  refetchAll: () => void;
  isLoading: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const LRSN_TOKEN_ADDRESS = (import.meta.env.VITE_LRSN_TOKEN_ADDRESS) as `0x${string}`;
const SUPPLY_API_URL = import.meta.env.VITE_SUPPLY_API_URL || '';
const SCAN_API_URL = import.meta.env.VITE_SCAN_API_URL || '';
const PRICE_API_URL = import.meta.env.VITE_PRICE_API_URL || `${SCAN_API_URL}/stats`;
const SILO_STATS_API_URL = import.meta.env.VITE_SILO_STATS_API_URL || '';
const BRIDGE_API_URL = import.meta.env.VITE_BRIDGE_API_URL || '';
const STAKING_CONTRACT_ADDRESS = import.meta.env.VITE_LRS_STAKING_CONTRACT_ADDRESS || '';

const REFRESH_INTERVALS = {
  STATS: 30000,      // 30 seconds
  BLOCKCHAIN: 30000, // 30 seconds
  SILO: 60000,       // 60 seconds
  BRIDGE: 15000,     // 15 seconds
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const safeNumber = (value: number, fallback: number = 0): number => {
  return (isNaN(value) || !isFinite(value)) ? fallback : value;
};

const calculateBlockReward = (blockHeight: number): number => {
  const newRewardApplyBlock = 639675;
  const newStakeRewardApplyBlock = 1952106;
  const reductionInterval = 46523;
  const reductionFactor = 0.9938;

  if (blockHeight < newRewardApplyBlock) {
    if (blockHeight < 2425846) return 10;
    if (blockHeight < 4851692) return 5;
    if (blockHeight < 7277538) return 2.5;
    if (blockHeight < 9703385) return 1.25;
    return 1;
  }

  let baseReward = 5;
  const blocksSinceReduction = blockHeight - newRewardApplyBlock;
  const numReductions = Math.floor(blocksSinceReduction / reductionInterval);

  for (let j = 0; j < numReductions; j++) {
    baseReward *= reductionFactor;
  }

  return blockHeight >= newStakeRewardApplyBlock ? baseReward * 0.8 : baseReward;
};

const calculateAPY = (stakedAmount: number, weeklyReward: number): number => {
  if (stakedAmount <= 0 || weeklyReward <= 0) return 0;
  const weeklyRate = weeklyReward / stakedAmount;
  return (Math.pow(1 + weeklyRate, 52) - 1) * 100;
};

const bytesToTerabytes = (bytes: string): number => {
  return parseFloat(bytes) / (1024 * 1024 * 1024 * 1024);
};

// ============================================================================
// CONTEXT
// ============================================================================

const GlobalDataContext = createContext<GlobalDataContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function GlobalDataProvider({children}: { children: React.ReactNode }) {
  const {address} = useAccount();
  const refreshTriggerRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  // ============================================================================
  // STATE - STATS DATA
  // ============================================================================
  const [stats, setStats] = useState<StatsData>({
    activeWallets: 0,
    price: 0,
    marketCap: 0,
    circulatingSupply: 0,
    lrsNodes: 0,
    totalValueLocked: 0,
    tvlPercentage: 0,
    isLoading: true,
    error: null,
  });

  // ============================================================================
  // STATE - BLOCKCHAIN DATA
  // ============================================================================
  const [blockchain, setBlockchain] = useState<BlockchainData>({
    currentBlockReward: 2.8768,
    currentBlock: 2000000,
    circulatingSupply: 12458932,
    totalSupply: 27063202,
    maxSupply: 45702970,
    isLoading: true,
    error: null,
  });

  // ============================================================================
  // STATE - SILO STATS
  // ============================================================================
  const [silo, setSilo] = useState<SiloStats>({
    totalSpace: 0,
    usedSpace: 0,
    freeSpace: 0,
    activeNodes: 0,
    networkUptime: 99.9,
    dataTransfer: 0,
    storageCost: 0.001,
    countries: [],
    isLoading: true,
    error: null,
  });

  // ============================================================================
  // STATE - STAKING DATA
  // ============================================================================
  const [stakingState, setStakingState] = useState({
    totalValueLocked: 0,
    circulatingSupply: 0,
    lrsTokenBalance: undefined as bigint | undefined,
    rewardPoolBalance: undefined as bigint | undefined,
    totalStakeCount: undefined as bigint | undefined,
    userStakeIds: undefined as bigint[] | undefined,
    currentStakeAmount: undefined as bigint | undefined,
    userStakes: [] as LRSStakeInfo[],
    isLoading: false,
  });

  // Wagmi hooks for staking
  const {data: lrsBalanceData, refetch: refetchLrsBalance} = useBalance({
    address: address,
    chainId: larissaChain.id,
    query: {enabled: !!address},
  });

  const {data: rewardPoolBalanceData} = useBalance({
    address: REWARD_POOL_TOKEN as Address,
    chainId: larissaChain.id,
    query: {enabled: true},
  });

  const {data: totalStakeCount} = useReadContract({
    address: LRS_STAKING_CONTRACT as Address,
    abi: LRS_STAKING_ABI,
    functionName: 'totalStakeCount',
    chainId: larissaChain.id,
    query: {enabled: true},
  });

  const {data: userStakeIds, refetch: refetchUserStakeIds} = useReadContract({
    address: LRS_STAKING_CONTRACT as Address,
    abi: LRS_STAKING_ABI,
    functionName: 'getUserAllStakes',
    args: address ? [address] : undefined,
    chainId: larissaChain.id,
    query: {enabled: !!address},
  });

  const {data: currentStakeAmount} = useReadContract({
    address: LRS_STAKING_CONTRACT as Address,
    abi: LRS_STAKING_ABI,
    functionName: 'getCurrentStakeAmount',
    chainId: larissaChain.id,
    query: {enabled: true},
  });

  // ============================================================================
  // STATE - LRSNODE DATA
  // ============================================================================
  const [lrsnodeState, setLrsnodeState] = useState({
    userReferCode: undefined as string | undefined,
    slotData: undefined as bigint | undefined,
    walletBalance: undefined as bigint | undefined,
    nodes: [] as NodeInfo[],
    referNodes: [] as ReferNodeInfo[],
    lockRewards: [] as LockRewardInfo[],
    isLoadingNodes: false,
    isLoadingReferNodes: false,
    isLoadingLockRewards: false,
  });

  const [lrsnodeStateV3, setLrsnodeStateV3] = useState({
    userReferCode: undefined as string | undefined,
    slotData: undefined as bigint | undefined,
    walletBalance: undefined as bigint | undefined,
    nodes: [] as NodeInfoV3[],
    referNodes: [] as ReferNodeInfo[],
    isLoadingNodes: false,
    isLoadingReferNodes: false,
  });

  const {data: userReferCodeData, refetch: refetchUserReferCode} = useReadContract({
    ...LRSNODE_CONTRACT_CONFIG,
    functionName: 'addressToReferCode',
    args: address ? [address] : undefined,
    query: {enabled: !!address},
  });

  const {data: slotData, refetch: refetchSlotData} = useReadContract({
    ...LRSNODE_CONTRACT_CONFIG,
    functionName: 'getKeySlots',
  });

  // V3 Contract hooks
  const {data: userReferCodeDataV3, refetch: refetchUserReferCodeV3} = useReadContract({
    ...LRSNODE_V3_CONTRACT_CONFIG,
    functionName: 'addressToReferCode',
    args: address ? [address] : undefined,
    query: {enabled: !!address},
  });

  // V3 uses per-plan stock instead of a global key slot counter.
  const slotDataV3 = undefined;

  const {data: balanceData, refetch: refetchBalance} = useBalance({
    address: address,
    query: {enabled: !!address},
  });

  // ============================================================================
  // STATE - BRIDGE DATA
  // ============================================================================
  const [bridgeState, setBridgeState] = useState({
    transactionHistory: [] as Array<{
      id: string;
      sourceChain: string;
      destinationChain: string;
      token: string;
      amount: string;
      status: string;
      timestamp: number;
      sourceHash?: string;
      destinationHash?: string;
      fromAddress?: string;
      toAddress?: string;
    }>,
    isLoadingHistory: false,
    historyError: null as string | null,
    chains: [] as Array<{
      id: number;
      name: string;
      icon?: string;
    }>,
    isLoadingChains: false,
  });

  // ============================================================================
  // STATE - MINT STATS DATA
  // ============================================================================
  const [mintStatsState, setMintStatsState] = useState({
    total1Minted: 0 as number,
    total2Minted: 0 as number,
    total3Minted: 0 as number,
    totalNFT: 0 as number,
    isLoading: false,
  });

  // ============================================================================
  // FETCH FUNCTIONS - STATS DATA
  // ============================================================================
  const fetchStatsData = useCallback(async () => {
    try {
      const isInitialLoad = isInitialLoadRef.current;
      setStats(prev => ({...prev, isLoading: isInitialLoad, error: null}));

      // Batch API calls in parallel
      const [supplyResponse, priceResponse, scanResponse] = await Promise.allSettled([
        fetch(SUPPLY_API_URL),
        fetch(PRICE_API_URL, {cache: 'no-store'}),
        fetch(`${SCAN_API_URL}/stats`),
      ]);

      // Process supply data
      let circulatingSupply = 0;
      if (supplyResponse.status === 'fulfilled' && supplyResponse.value.ok) {
        const supplyData = await supplyResponse.value.json();
        circulatingSupply = safeNumber(parseFloat(supplyData.circulating_supply || '0'));
      }

      // Process price data
      let price = 0;
      if (priceResponse.status === 'fulfilled' && priceResponse.value.ok) {
        const priceData = await priceResponse.value.json();
        price = safeNumber(parseFloat(priceData.coin_price || '0'));
      }

      const marketCap = safeNumber(price * circulatingSupply);

      // Process wallet count
      let activeWallets = 0;
      if (scanResponse.status === 'fulfilled' && scanResponse.value.ok) {
        const scanData = await scanResponse.value.json();
        activeWallets = safeNumber(parseInt(scanData.total_addresses || '0'));
      }

      // Process TVL
      let totalValueLocked = 0;
      let tvlPercentage = 0;
      try {
        const tvlResponse = await fetch(`${SCAN_API_URL}/addresses/${STAKING_CONTRACT_ADDRESS}`);
        if (tvlResponse.ok) {
          const tvlData = await tvlResponse.json();
          const balanceWei = parseFloat(tvlData.coin_balance || '0');
          totalValueLocked = safeNumber(balanceWei / 1e18);
          tvlPercentage = circulatingSupply > 0 ? safeNumber((totalValueLocked / circulatingSupply) * 100) : 0;
        }
      } catch (tvlError) {
        console.warn('Could not fetch TVL data:', tvlError);
      }

      // Fetch LRS nodes from contract
      let lrsNodes = 0;
      try {
        const node3Ready =
          !!LRSNODE_V3_CONTRACT_CONFIG.address &&
          LRSNODE_V3_CONTRACT_CONFIG.address.length > 2;

        if (node3Ready) {
          const lastNodeKeyID = await publicClient.readContract({
            address: LRSNODE_V3_CONTRACT_CONFIG.address,
            abi: [
              {
                name: 'lastNodeKeyID',
                type: 'function',
                stateMutability: 'view',
                inputs: [],
                outputs: [{type: 'uint256'}],
              },
            ] as const,
            functionName: 'lastNodeKeyID',
          });

          // Node3 node count comes from `lastNodeKeyID` (total keys issued)
          lrsNodes = safeNumber(Number(lastNodeKeyID));
        } else {
          const currentKeyBuy = await publicClient.readContract({
            ...LRSNODE_CONTRACT_CONFIG,
            functionName: 'currentKeyBuy',
          });
          lrsNodes = safeNumber(Number(currentKeyBuy));
        }
      } catch (e) {
        console.error('Error fetching LRS nodes:', e);
      }

      setStats({
        activeWallets,
        price,
        marketCap,
        circulatingSupply,
        lrsNodes,
        totalValueLocked,
        tvlPercentage,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching stats data:', error);
      setStats(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, []);

  // ============================================================================
  // FETCH FUNCTIONS - BLOCKCHAIN DATA
  // ============================================================================
  const fetchBlockchainData = useCallback(async () => {
    try {
      const isInitialLoad = isInitialLoadRef.current;
      setBlockchain(prev => ({...prev, isLoading: isInitialLoad, error: null}));

      // Fetch supply and block in parallel
      const [supplyResponse, blockResponse] = await Promise.allSettled([
        fetch(SUPPLY_API_URL),
        fetch(larissaChain.rpcUrls.default.http[0], {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            jsonrpc: '2.0',
            method: 'eth_blockNumber',
            params: [],
            id: 1,
          }),
        }),
      ]);

      let circulatingSupply = blockchain.circulatingSupply;
      let totalSupply = blockchain.totalSupply;
      let maxSupply = blockchain.maxSupply;
      let currentBlock = blockchain.currentBlock;

      if (supplyResponse.status === 'fulfilled' && supplyResponse.value.ok) {
        const supplyData = await supplyResponse.value.json();
        circulatingSupply = safeNumber(parseFloat(supplyData.circulating_supply || '0'), blockchain.circulatingSupply);
        totalSupply = safeNumber(parseFloat(supplyData.total_supply || '0'), blockchain.totalSupply);
      }

      if (blockResponse.status === 'fulfilled' && blockResponse.value.ok) {
        const blockData = await blockResponse.value.json();
        currentBlock = parseInt(blockData.result, 16) || blockchain.currentBlock;
      }

      const currentBlockReward = calculateBlockReward(currentBlock);

      setBlockchain({
        currentBlockReward,
        currentBlock,
        circulatingSupply,
        totalSupply,
        maxSupply,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching blockchain data:', error);
      setBlockchain(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, [blockchain.circulatingSupply, blockchain.totalSupply, blockchain.maxSupply, blockchain.currentBlock]);

  // ============================================================================
  // FETCH FUNCTIONS - SILO STATS
  // ============================================================================
  const fetchSiloStats = useCallback(async () => {
    console.log('fetchSiloStats  ====== ')
    try {
      const isInitialLoad = isInitialLoadRef.current;
      setSilo(prev => ({...prev, isLoading: isInitialLoad, error: null}));

      const response = await fetch(SILO_STATS_API_URL, {
        method: 'GET',
        headers: {'Accept': 'application/json'},
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch SILO stats: ${response.status}`);
      }

      const data = await response.json();
      const countryData = data.nodesByCountry || [];
      const totalCountryNodes = countryData.reduce(
        (sum: number, country: { totalNodes: string }) => sum + parseInt(country.totalNodes || '0'),
        0
      );

      const countries = countryData
      .map((country: { countryCode: string; totalNodes: string }) => {
        const nodes = parseInt(country.totalNodes || '0');
        return {
          countryCode: country.countryCode,
          nodes,
          percentage: totalCountryNodes > 0 ? (nodes / totalCountryNodes) * 100 : 0,
        };
      })
      .sort((a: { nodes: number }, b: { nodes: number }) => b.nodes - a.nodes);

      setSilo({
        totalSpace: bytesToTerabytes(data.totalStorage || '0'),
        usedSpace: bytesToTerabytes(data.totalUsedStorage || '0'),
        freeSpace: bytesToTerabytes(data.totalFreeStorage || '0'),
        activeNodes: data.totalNodes || 0,
        networkUptime: 99.9,
        dataTransfer: 0,
        storageCost: 0.001,
        countries,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching SILO stats:', error);
      setSilo(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));
    }
  }, []);

  // ============================================================================
  // FETCH FUNCTIONS - STAKING DATA
  // ============================================================================
  const fetchStakeDetails = useCallback(async (stakeIds: bigint[]) => {
    if (!stakeIds || stakeIds.length === 0) return [];

    try {
      const client = createPublicClient({
        chain: larissaChain,
        transport: http(),
      });

      const stakePromises = stakeIds.map((stakeId) =>
        client.readContract({
          address: LRS_STAKING_CONTRACT as Address,
          abi: LRS_STAKING_ABI,
          functionName: 'userStakes',
          args: [stakeId],
        })
      );

      const stakeResults = await Promise.all(stakePromises);
      return stakeResults.map((stakeData) => ({
        stakeID: (stakeData as any)[0],
        stakeAddress: (stakeData as any)[1],
        stakeAmt: (stakeData as any)[2],
        stakeStartTime: (stakeData as any)[3],
        stakeStatus: (stakeData as any)[4],
      }));
    } catch (error) {
      console.error('Error fetching stake details:', error);
      return [];
    }
  }, []);

  // Update staking state when wagmi hooks update
  useEffect(() => {
    const updateStakingState = async () => {
      const lrsTokenBalance = lrsBalanceData?.value;
      const rewardPoolBalance = rewardPoolBalanceData?.value;
      const totalStakeCountValue = totalStakeCount as bigint | undefined;
      const userStakeIdsValue = userStakeIds as bigint[] | undefined;
      const currentStakeAmountValue = currentStakeAmount as bigint | undefined;

      let userStakes: LRSStakeInfo[] = [];
      if (userStakeIdsValue && userStakeIdsValue.length > 0) {
        const isInitialLoad = isInitialLoadRef.current;
        setStakingState(prev => ({...prev, isLoading: isInitialLoad}));
        userStakes = await fetchStakeDetails(userStakeIdsValue);
        setStakingState(prev => ({...prev, isLoading: false}));
      }

      // Update from stats for TVL and circulating supply
      const totalValueLocked = stats.totalValueLocked;
      const circulatingSupply = stats.circulatingSupply;

      // Calculate reward per stake
      const rewardPerStake =
        rewardPoolBalance && totalStakeCountValue && totalStakeCountValue > BigInt(0)
          ? Number(formatUnits(rewardPoolBalance, 18)) / (Number(totalStakeCountValue) + 1)
          : 0;

      // Calculate APY
      const currentStakeAmountNumber = currentStakeAmountValue
        ? Number(formatUnits(currentStakeAmountValue, 18))
        : 0;
      const calculatedAPY =
        currentStakeAmountNumber > 0 && rewardPerStake > 0
          ? calculateAPY(currentStakeAmountNumber, rewardPerStake)
          : 0;

      setStakingState({
        totalValueLocked,
        circulatingSupply,
        lrsTokenBalance,
        rewardPoolBalance,
        totalStakeCount: totalStakeCountValue,
        userStakeIds: userStakeIdsValue,
        currentStakeAmount: currentStakeAmountValue,
        userStakes,
        isLoading: false,
      });
    };

    updateStakingState();
  }, [
    lrsBalanceData,
    rewardPoolBalanceData,
    totalStakeCount,
    userStakeIds,
    currentStakeAmount,
    stats.totalValueLocked,
    stats.circulatingSupply,
    fetchStakeDetails,
  ]);

  // ============================================================================
  // FETCH FUNCTIONS - LRSNODE DATA
  // ============================================================================
  const fetchNodes = useCallback(async () => {
    if (!address) {
      setLrsnodeState(prev => ({...prev, nodes: [], isLoadingNodes: false}));
      return;
    }

    try {
      const isInitialLoad = isInitialLoadRef.current;
      setLrsnodeState(prev => ({...prev, isLoadingNodes: isInitialLoad}));

      const nodesList = await publicClient.readContract({
        ...LRSNODE_CONTRACT_CONFIG,
        functionName: 'getBuyNodesList',
        args: [address],
      }) as any[];

      if (!nodesList || nodesList.length === 0) {
        setLrsnodeState(prev => ({...prev, nodes: [], isLoadingNodes: false}));
        return;
      }

      // Fetch pending rewards for all nodes in parallel
      const nodesWithRewards = await Promise.all(
        nodesList.map(async (node) => {
          try {
            const pendingReward = await publicClient.readContract({
              ...LRSNODE_CONTRACT_CONFIG,
              functionName: 'pendingRewards',
              args: [node.nodeKey],
            });
            return {...node, pendingReward: pendingReward as bigint};
          } catch (error) {
            console.error(`Error fetching reward for node ${node.nodeKey}:`, error);
            return {...node, pendingReward: BigInt(0)};
          }
        })
      );

      setLrsnodeState(prev => ({...prev, nodes: nodesWithRewards, isLoadingNodes: false}));
    } catch (error) {
      console.error('Error fetching nodes:', error);
      setLrsnodeState(prev => ({...prev, nodes: [], isLoadingNodes: false}));
    }
  }, [address]);

  const fetchReferNodes = useCallback(async (referCode: string | undefined) => {
    if (!referCode || referCode.length === 0) {
      setLrsnodeState(prev => ({...prev, referNodes: [], isLoadingReferNodes: false}));
      return;
    }

    try {
      const isInitialLoad = isInitialLoadRef.current;
      setLrsnodeState(prev => ({...prev, isLoadingReferNodes: isInitialLoad}));

      const referredNodes = await publicClient.readContract({
        ...LRSNODE_CONTRACT_CONFIG,
        functionName: 'getReferNodesList',
        args: [referCode],
      }) as string[];

      if (!referredNodes || referredNodes.length === 0) {
        setLrsnodeState(prev => ({...prev, referNodes: [], isLoadingReferNodes: false}));
        return;
      }

      // Fetch claim status for all nodes in parallel
      const nodesWithStatus = await Promise.all(
        referredNodes.map(async (nodeKey) => {
          try {
            const isClaimed = await publicClient.readContract({
              ...LRSNODE_CONTRACT_CONFIG,
              functionName: 'referClaim',
              args: [nodeKey],
            });
            return {nodeKey, isClaimed: !!isClaimed};
          } catch (error) {
            console.error(`Error fetching claim status for ${nodeKey}:`, error);
            return {nodeKey, isClaimed: false};
          }
        })
      );

      setLrsnodeState(prev => ({...prev, referNodes: nodesWithStatus, isLoadingReferNodes: false}));
    } catch (error) {
      console.error('Failed to fetch refer nodes:', error);
      setLrsnodeState(prev => ({...prev, referNodes: [], isLoadingReferNodes: false}));
    }
  }, []);

  const fetchLockRewards = useCallback(async () => {
    if (!address) {
      setLrsnodeState(prev => ({...prev, lockRewards: [], isLoadingLockRewards: false}));
      return;
    }

    try {
      const isInitialLoad = isInitialLoadRef.current;
      setLrsnodeState(prev => ({...prev, isLoadingLockRewards: isInitialLoad}));

      const lockRewardsList = await publicClient.readContract({
        ...LRSNODE_CONTRACT_CONFIG,
        functionName: 'getLockRewardList',
        args: [address],
      }) as LockRewardInfo[];

      setLrsnodeState(prev => ({
        ...prev,
        lockRewards: lockRewardsList || [],
        isLoadingLockRewards: false,
      }));
    } catch (error) {
      console.error('Failed to fetch lock rewards:', error);
      setLrsnodeState(prev => ({...prev, lockRewards: [], isLoadingLockRewards: false}));
    }
  }, [address]);

  // Update LRSNode state when wagmi hooks update
  useEffect(() => {
    setLrsnodeState(prev => ({
      ...prev,
      userReferCode: userReferCodeData as string | undefined,
      slotData: slotData as bigint | undefined,
      walletBalance: balanceData?.value,
    }));
  }, [userReferCodeData, slotData, balanceData]);

  // Fetch nodes when address changes
  useEffect(() => {
    fetchNodes();
  }, [address, fetchNodes, refreshTriggerRef.current]);

  // Fetch refer nodes when refer code changes
  useEffect(() => {
    fetchReferNodes(lrsnodeState.userReferCode);
  }, [lrsnodeState.userReferCode, fetchReferNodes, refreshTriggerRef.current]);

  // Fetch lock rewards when address changes
  useEffect(() => {
    fetchLockRewards();
  }, [address, fetchLockRewards, refreshTriggerRef.current]);

  // ============================================================================
  // FETCH FUNCTIONS - LRSNODE V3 DATA
  // ============================================================================
  const fetchNodesV3 = useCallback(async () => {
    if (!address) {
      setLrsnodeStateV3(prev => ({...prev, nodes: [], isLoadingNodes: false}));
      return;
    }

    try {
      const isInitialLoad = isInitialLoadRef.current;
      setLrsnodeStateV3(prev => ({...prev, isLoadingNodes: isInitialLoad}));

      const nodesList = await publicClient.readContract({
        ...LRSNODE_V3_CONTRACT_CONFIG,
        functionName: 'getBuyNodesList',
        args: [address],
      }) as any[];

      if (!nodesList || nodesList.length === 0) {
        setLrsnodeStateV3(prev => ({...prev, nodes: [], isLoadingNodes: false}));
        return;
      }

      // Fetch pending rewards for all nodes in parallel
      const nodesWithRewards = await Promise.all(
        nodesList.map(async (node) => {
          try {
            const pendingReward = await publicClient.readContract({
              ...LRSNODE_V3_CONTRACT_CONFIG,
              functionName: 'pendingRewards',
              args: [node.nodeKey],
            });
            return {...node, pendingReward: pendingReward as bigint};
          } catch (error) {
            console.error(`Error fetching reward for node ${node.nodeKey}:`, error);
            return {...node, pendingReward: BigInt(0)};
          }
        })
      );

      setLrsnodeStateV3(prev => ({...prev, nodes: nodesWithRewards, isLoadingNodes: false}));
    } catch (error) {
      console.error('Error fetching V3 nodes:', error);
      setLrsnodeStateV3(prev => ({...prev, nodes: [], isLoadingNodes: false}));
    }
  }, [address]);

  const fetchReferNodesV3 = useCallback(async (referCode: string | undefined) => {
    if (!referCode || referCode.length === 0) {
      setLrsnodeStateV3(prev => ({...prev, referNodes: [], isLoadingReferNodes: false}));
      return;
    }

    try {
      const isInitialLoad = isInitialLoadRef.current;
      setLrsnodeStateV3(prev => ({...prev, isLoadingReferNodes: isInitialLoad}));

      const referredNodes = await publicClient.readContract({
        ...LRSNODE_V3_CONTRACT_CONFIG,
        functionName: 'getReferNodesList',
        args: [referCode],
      }) as string[];

      if (!referredNodes || referredNodes.length === 0) {
        setLrsnodeStateV3(prev => ({...prev, referNodes: [], isLoadingReferNodes: false}));
        return;
      }

      // Fetch claim status for all nodes in parallel
      const nodesWithStatus = await Promise.all(
        referredNodes.map(async (nodeKey) => {
          try {
            const isClaimed = await publicClient.readContract({
              ...LRSNODE_V3_CONTRACT_CONFIG,
              functionName: 'referClaim',
              args: [nodeKey],
            });
            return {nodeKey, isClaimed: !!isClaimed};
          } catch (error) {
            console.error(`Error fetching claim status for ${nodeKey}:`, error);
            return {nodeKey, isClaimed: false};
          }
        })
      );

      setLrsnodeStateV3(prev => ({...prev, referNodes: nodesWithStatus, isLoadingReferNodes: false}));
    } catch (error) {
      console.error('Failed to fetch V3 refer nodes:', error);
      setLrsnodeStateV3(prev => ({...prev, referNodes: [], isLoadingReferNodes: false}));
    }
  }, []);

  // Update LRSNode V3 state when wagmi hooks update
  useEffect(() => {
    setLrsnodeStateV3(prev => ({
      ...prev,
      userReferCode: userReferCodeDataV3 as string | undefined,
      slotData: slotDataV3 as bigint | undefined,
      walletBalance: balanceData?.value,
    }));
  }, [userReferCodeDataV3, balanceData]);

  // Fetch V3 nodes when address changes
  useEffect(() => {
    fetchNodesV3();
  }, [address, fetchNodesV3, refreshTriggerRef.current]);

  // Fetch V3 refer nodes when refer code changes
  useEffect(() => {
    fetchReferNodesV3(lrsnodeStateV3.userReferCode);
  }, [lrsnodeStateV3.userReferCode, fetchReferNodesV3, refreshTriggerRef.current]);

  // ============================================================================
  // FETCH FUNCTIONS - MINT STATS DATA
  // ============================================================================
  const fetchMintStats = useCallback(async () => {
    try {
      const isInitialLoad = isInitialLoadRef.current;
      setMintStatsState(prev => ({...prev, isLoading: isInitialLoad}));

      // Validate and checksum the address
      let contractAddress: Address;
      try {
        contractAddress = getAddress(MINT_STATS_CONTRACT);
      } catch (addressError) {
        console.error('Invalid contract address:', MINT_STATS_CONTRACT);
        console.error('Address must be 42 characters (0x + 40 hex characters)');
        setMintStatsState(prev => ({
          ...prev,
          isLoading: false,
        }));
        return;
      }

      // Fetch all three values in parallel
      const [totalL1Minted, totalL2Minted, totalL3Minted] = await Promise.all([
        publicClient.readContract({
          address: contractAddress,
          abi: MINT_STATS_ABI,
          functionName: 'totalL1Minted',
        }),
        publicClient.readContract({
          address: contractAddress,
          abi: MINT_STATS_ABI,
          functionName: 'totalL2Minted',
        }),
        publicClient.readContract({
          address: contractAddress,
          abi: MINT_STATS_ABI,
          functionName: 'totalL3Minted',
        }),
      ]);

      const total1Minted = Number(totalL1Minted as bigint);
      const total2Minted = Number(totalL2Minted as bigint);
      const total3Minted = Number(totalL3Minted as bigint);

      const totalNFT = total1Minted + total2Minted + total3Minted;

      // Convert bigint to number
      setMintStatsState({
        total1Minted,
        total2Minted,
        total3Minted,
        totalNFT,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching mint stats:', error);
      setMintStatsState(prev => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, []);

  // ============================================================================
  // FETCH FUNCTIONS - BRIDGE DATA
  // ============================================================================

  // Fetch chains from API
  const fetchBridgeChains = useCallback(async () => {
    try {
      setBridgeState(prev => ({...prev, isLoadingChains: true}));

      const response = await fetch(`${BRIDGE_API_URL}/getChains`);
      if (!response.ok) {
        throw new Error('Failed to fetch chains');
      }

      const result = await response.json();

      if (result.status && result.data) {
        setBridgeState(prev => ({
          ...prev,
          chains: result.data,
          isLoadingChains: false,
        }));
      } else {
        setBridgeState(prev => ({...prev, isLoadingChains: false}));
      }
    } catch (err: any) {
      console.error('Error fetching bridge chains:', err);
      setBridgeState(prev => ({...prev, isLoadingChains: false}));
    }
  }, []);

  // Fetch bridge transactions with filters (new API)
  const fetchBridgeTransactions = useCallback(async (filters: {
    page?: number;
    limit?: number;
    sourceChain?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
    address?: string;
    search?: string;
  }) => {
    try {
      const params = new URLSearchParams();

      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sourceChain) params.append('sourceChain', filters.sourceChain);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.status !== undefined && filters.status !== '') params.append('status', filters.status);
      if (filters.address) params.append('address', filters.address);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${BRIDGE_API_URL}/getBridgeTransactions?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch bridge transactions');
      }

      const result = await response.json();
      return result;
    } catch (err: any) {
      console.error('Error fetching bridge transactions:', err);
      return {
        status: false,
        data: [],
        total: 0,
        totalPages: 0,
        message: err.message || 'Failed to fetch bridge transactions',
      };
    }
  }, []);

  // Process transaction (create deposit record)
  const processTransaction = useCallback(async (txHash: string, chainId?: number) => {
    try {
      const params = new URLSearchParams();
      params.append('txHash', txHash);
      if (chainId) {
        params.append('chainId', chainId.toString());
      }

      const response = await fetch(`${BRIDGE_API_URL}/processTransaction?${params}`);
      if (!response.ok) {
        throw new Error('Failed to process transaction');
      }

      return await response.json();
    } catch (err: any) {
      console.error('Error processing transaction:', err);
      return {
        status: false,
        message: err.message || 'Failed to process transaction',
      };
    }
  }, []);

  // Fetch bridge history for connected wallet (existing API - backward compatibility)
  const fetchBridgeHistory = useCallback(async () => {
    if (!address) {
      setBridgeState(prev => ({...prev, transactionHistory: [], isLoadingHistory: false, historyError: null}));
      return;
    }

    try {
      const isInitialLoad = isInitialLoadRef.current;
      setBridgeState(prev => ({...prev, isLoadingHistory: isInitialLoad, historyError: null}));

      const response = await fetch(`${BRIDGE_API_URL}/getTrxList?address=${address}`);
      if (!response.ok) {
        throw new Error('Failed to fetch transaction history');
      }

      const data = await response.json();
      const txList = Array.isArray(data) ? data : (data.data || data.transactions || []);

      const getStatusText = (statusCode: string) => {
        switch (statusCode) {
          case '1':
          case '2':
            return 'pending';
          case '3':
          case '5':
            return 'completed';
          case '4':
            return 'failed';
          default:
            return 'pending';
        }
      };

      // Process transactions with full format support
      const enrichedTransactions = txList.map((tx: any) => {
        // Handle both old and new API formats
        const fromChainId = parseInt(tx.transactionDepositSourceChain || tx.fromChainId || '0');
        const toChainId = parseInt(tx.transactionDepositDestinationChain || tx.toChainId || '0');

        // Convert timestamp (API may return milliseconds or seconds)
        const timestamp = tx.transactionDepositTimestamp
          ? parseInt(tx.transactionDepositTimestamp) / 1000
          : tx.createdAt
            ? new Date(tx.createdAt).getTime() / 1000
            : tx.timestamp
              ? (tx.timestamp > 1e10 ? tx.timestamp / 1000 : tx.timestamp)
              : Date.now() / 1000;

        return {
          id: tx.transactionID || tx.id || tx._id || `${tx.transactionDepositHash || tx.txHash || tx.hash}-${timestamp}`,
          sourceChain: tx.sourceChain || tx.fromChainName || 'Unknown',
          destinationChain: tx.destinationChain || tx.toChainName || 'Unknown',
          token: tx.token || tx.tokenSymbol || tx.symbol || 'Unknown',
          amount: tx.transactionDepositAmount || tx.amount || '0',
          status: getStatusText(tx.transactionStatus || tx.status || '0'),
          timestamp,
          sourceHash: tx.transactionDepositHash || tx.sourceHash || tx.sourceTxHash || tx.txHash || tx.hash,
          destinationHash: tx.transactionDestinationHash || tx.destinationHash || tx.destinationTxHash,
          fromAddress: tx.fromAddress || tx.sender || tx.transactionDepositSender,
          toAddress: tx.toAddress || tx.recipient || tx.transactionDepositRecipient,
        };
      });

      // Sort by timestamp (latest first)
      enrichedTransactions.sort((a: { timestamp: number }, b: { timestamp: number }) => b.timestamp - a.timestamp);

      setBridgeState(prev => ({
        ...prev,
        transactionHistory: enrichedTransactions,
        isLoadingHistory: false,
        historyError: null,
      }));
    } catch (err: any) {
      console.error('Error fetching transaction history:', err);
      setBridgeState(prev => ({
        ...prev,
        transactionHistory: [],
        isLoadingHistory: false,
        historyError: err.message || 'Failed to load transaction history',
      }));
    }
  }, [address]);

  // ============================================================================
  // INITIAL FETCH & INTERVALS
  // ============================================================================
  useEffect(() => {
    // Initial fetch
    fetchStatsData();
    fetchBlockchainData();
    fetchSiloStats();
    fetchMintStats();

    // Mark initial load as complete after a short delay to allow all initial fetches to show loading
    const initialLoadTimeout = setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 2000); // 2 seconds should be enough for initial fetches to start

    // Set up intervals
    const statsInterval = setInterval(fetchStatsData, REFRESH_INTERVALS.STATS);
    const blockchainInterval = setInterval(fetchBlockchainData, REFRESH_INTERVALS.BLOCKCHAIN);
    const siloInterval = setInterval(fetchSiloStats, REFRESH_INTERVALS.SILO);
    const mintStatsInterval = setInterval(fetchMintStats, REFRESH_INTERVALS.STATS);

    return () => {
      clearTimeout(initialLoadTimeout);
      clearInterval(statsInterval);
      clearInterval(blockchainInterval);
      clearInterval(siloInterval);
      clearInterval(mintStatsInterval);
    };
  }, [fetchStatsData, fetchBlockchainData, fetchSiloStats, fetchMintStats]);

  // Load chains on mount
  useEffect(() => {
    fetchBridgeChains();
  }, [fetchBridgeChains]);

  useEffect(() => {
    if (address) {
      fetchBridgeHistory();
      const bridgeInterval = setInterval(fetchBridgeHistory, REFRESH_INTERVALS.BRIDGE);
      return () => clearInterval(bridgeInterval);
    } else {
      setBridgeState(prev => ({...prev, transactionHistory: [], isLoadingHistory: false, historyError: null}));
    }
  }, [address, fetchBridgeHistory]);

  // ============================================================================
  // CALCULATED VALUES
  // ============================================================================
  const rewardPerStake =
    stakingState.rewardPoolBalance &&
    stakingState.totalStakeCount &&
    stakingState.totalStakeCount > BigInt(0)
      ? Number(formatUnits(stakingState.rewardPoolBalance, 18)) / (Number(stakingState.totalStakeCount) + 1)
      : 0;

  const currentStakeAmountNumber = stakingState.currentStakeAmount
    ? Number(formatUnits(stakingState.currentStakeAmount, 18))
    : 0;

  const calculatedAPY =
    currentStakeAmountNumber > 0 && rewardPerStake > 0
      ? calculateAPY(currentStakeAmountNumber, rewardPerStake)
      : 0;

  // ============================================================================
  // REFETCH FUNCTIONS
  // ============================================================================
  const refreshNodes = useCallback(async () => {
    refreshTriggerRef.current += 1;
    await fetchNodes();
  }, [fetchNodes]);

  const refreshNodesV3 = useCallback(async () => {
    refreshTriggerRef.current += 1;
    await fetchNodesV3();
  }, [fetchNodesV3]);

  const refetchAll = useCallback(() => {
    fetchStatsData();
    fetchBlockchainData();
    fetchMintStats();
    refetchLrsBalance();
    refetchUserStakeIds();
    refetchUserReferCode();
    refetchSlotData();
    refetchBalance();
    refreshNodes();
    if (address) {
      fetchBridgeHistory();
    }
  }, [
    fetchStatsData,
    fetchBlockchainData,
    fetchMintStats,
    refetchLrsBalance,
    refetchUserStakeIds,
    refetchUserReferCode,
    refetchSlotData,
    refetchBalance,
    refreshNodes,
    address,
    fetchBridgeHistory,
  ]);

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  const isLoading =
    stats.isLoading ||
    blockchain.isLoading ||
    silo.isLoading ||
    stakingState.isLoading ||
    lrsnodeState.isLoadingNodes ||
    lrsnodeState.isLoadingReferNodes ||
    lrsnodeState.isLoadingLockRewards ||
    bridgeState.isLoadingHistory ||
    mintStatsState.isLoading;

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  const value: GlobalDataContextType = {
    stats,
    blockchain,
    silo,
    staking: {
      totalValueLocked: stakingState.totalValueLocked,
      circulatingSupply: stakingState.circulatingSupply,
      lrsTokenBalance: stakingState.lrsTokenBalance,
      rewardPoolBalance: stakingState.rewardPoolBalance,
      totalStakeCount: stakingState.totalStakeCount,
      userStakeIds: stakingState.userStakeIds,
      currentStakeAmount: stakingState.currentStakeAmount,
      userStakes: stakingState.userStakes,
      rewardPerStake,
      calculatedAPY,
      refetchLrsBalance,
      refetchUserStakeIds,
      refetchAll: () => {
        refetchLrsBalance();
        refetchUserStakeIds();
      },
      isLoading: stakingState.isLoading,
    },
    lrsnode: {
      userReferCode: lrsnodeState.userReferCode,
      slotData: lrsnodeState.slotData,
      walletBalance: lrsnodeState.walletBalance,
      nodes: lrsnodeState.nodes,
      referNodes: lrsnodeState.referNodes,
      lockRewards: lrsnodeState.lockRewards,
      isLoadingNodes: lrsnodeState.isLoadingNodes,
      isLoadingReferNodes: lrsnodeState.isLoadingReferNodes,
      isLoadingLockRewards: lrsnodeState.isLoadingLockRewards,
      refreshNodes,
    },
    lrsnodeV3: {
      userReferCode: lrsnodeStateV3.userReferCode,
      slotData: lrsnodeStateV3.slotData,
      walletBalance: lrsnodeStateV3.walletBalance,
      nodes: lrsnodeStateV3.nodes,
      referNodes: lrsnodeStateV3.referNodes,
      isLoadingNodes: lrsnodeStateV3.isLoadingNodes,
      isLoadingReferNodes: lrsnodeStateV3.isLoadingReferNodes,
      refreshNodes: refreshNodesV3,
    },
    bridge: {
      transactionHistory: bridgeState.transactionHistory,
      isLoadingHistory: bridgeState.isLoadingHistory,
      historyError: bridgeState.historyError,
      refetchHistory: fetchBridgeHistory,
      chains: bridgeState.chains,
      isLoadingChains: bridgeState.isLoadingChains,
      fetchBridgeTransactions,
      processTransaction,
    },
    mintStats: {
      total1Minted: mintStatsState.total1Minted,
      total2Minted: mintStatsState.total2Minted,
      total3Minted: mintStatsState.total3Minted,
      totalNFT: mintStatsState.totalNFT,
      isLoading: mintStatsState.isLoading,
      refetch: fetchMintStats,
    },
    refetchAll,
    isLoading,
  };

  return <GlobalDataContext.Provider value={value}>{children}</GlobalDataContext.Provider>;
}

// ============================================================================
// HOOKS
// ============================================================================

export function useGlobalData() {
  const context = useContext(GlobalDataContext);
  if (context === undefined) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
}

export function useStats() {
  const {stats} = useGlobalData();
  return stats;
}

export function useBlockchain() {
  const {blockchain} = useGlobalData();
  return blockchain;
}

export function useSilo() {
  const {silo} = useGlobalData();
  return silo;
}

export function useStaking() {
  const {staking} = useGlobalData();
  return staking;
}

export function useLRSNode() {
  const {lrsnode} = useGlobalData();
  return lrsnode;
}

export function useLRSNodeV3() {
  const {lrsnodeV3} = useGlobalData();
  return lrsnodeV3;
}

export function useBridge() {
  const {bridge} = useGlobalData();
  return bridge;
}

export function useMintStats() {
  const {mintStats} = useGlobalData();
  return mintStats;
}
